interface ValidationIssue {
  message: string;
}

type ValidationResult =
  | { success: true }
  | { success: false; error: { issues: ValidationIssue[] } };

interface ValidationSchema {
  safeParse(value: unknown): ValidationResult;
}

const createSchema = (
  validate: (value: unknown) => string
): ValidationSchema => ({
  safeParse(value: unknown): ValidationResult {
    const message = validate(value);
    if (!message) return { success: true };
    return { success: false, error: { issues: [{ message }] } };
  }
});

export const parseValidationError = (
  schema: ValidationSchema,
  value: unknown
): string => {
  const result = schema.safeParse(value);
  return result.success ? "" : result.error.issues[0]?.message ?? "";
};

const getString = (value: unknown): string =>
  typeof value === "string" ? value : "";

export const emailSchema = (isRequired = false): ValidationSchema =>
  createSchema((value) => {
    const email = getString(value);
    if (!email) {
      return isRequired ? "bookingV4.doc.email.errorRequired" : "";
    }
    return email.length <= 100 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      ? ""
      : "bookingV4.doc.email.errorInvalid";
  });

export const idNumberSchema = (isRequired = false): ValidationSchema =>
  createSchema((value) => {
    const idNumber = getString(value);
    if (!idNumber) {
      return isRequired ? "bookingV4.doc.idNumber.errorRequired" : "";
    }
    return idNumber.length <= 100 && /^[a-zA-Z0-9]+$/.test(idNumber)
      ? ""
      : "bookingV4.doc.idNumber.errorInvalid";
  });

export const taxNumberSchema = (isRequired = false, isFtz = false): ValidationSchema =>
  createSchema((value) => {
    const taxNumber = getString(value);
    if (!taxNumber) {
      return isRequired ? "bookingV4.doc.taxNumber.errorRequired" : "";
    }
    if (isFtz) {
      return taxNumber.length < 15 ? "bookingV4.doc.taxNumber.errorMin" : ""
    }
    return taxNumber.length <= 100 && /^[a-zA-Z0-9]+$/.test(taxNumber)
      ? ""
      : "bookingV4.doc.taxNumber.errorInvalid";
  });

export const isImageValuePresent = (value: unknown): boolean => {
  if (typeof value === "string") return value.trim().length > 0;
  return typeof File !== "undefined" && value instanceof File;
};

export const imageRequiredError = (
  isRequired: boolean,
  value: unknown
): string => {
  if (!isRequired) return "";
  return isImageValuePresent(value)
    ? ""
    : "bookingV4.doc.upload.errorRequired";
};

export const dutyTaxTermsSchema = (): ValidationSchema =>
  createSchema((value) =>
    value === "DAP" || value === "DDP"
      ? ""
      : "bookingV4.doc.dutyTaxTerms.errorRequired"
  );

export const ADDITIONAL_DOCUMENT_ACCEPTED_TYPES = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "application/pdf"
];
export const ADDITIONAL_DOCUMENT_MAX_SIZE_MB = 5;
export const ADDITIONAL_DOCUMENT_MAX_COUNT = 5;

export const additionalDocumentFileSchema: ValidationSchema = createSchema(
  (value) => {
    if (typeof File === "undefined" || !(value instanceof File)) {
      return "bookingV4.doc.upload.limitMessage";
    }
    if (!ADDITIONAL_DOCUMENT_ACCEPTED_TYPES.includes(value.type)) {
      return "bookingV4.doc.upload.limitMessage";
    }
    return value.size <= ADDITIONAL_DOCUMENT_MAX_SIZE_MB * 1024 * 1024
      ? ""
      : "bookingV4.doc.upload.limitMessage";
  }
);
